/**
 * Analytics Event Validation Utilities
 *
 * Provides Yup schema validation for all analytics events.
 * Ensures event parameters conform to expected structure before sending to PostHog.
 * @module features/analytics/utils/validator
 */
import { boolean, number, object, string } from "yup";

import { ANALYTICS_EVENTS } from "../constants";
import type { TEventParameter } from "../types";

/**
 * Validation schemas for analytics events.
 *
 * Maps event names to Yup validation schemas for parameter validation.
 * Each schema defines the required and optional fields for its event type.
 */
export const VALIDATION_SCHEMAS: Readonly<
  Record<string, ReturnType<typeof object>>
> = {
  // Core Events
  [ANALYTICS_EVENTS.CORE.SCREEN_VIEW]: object({
    screen_name: string().required(),
    screen_class: string().required(),
  }),

  [ANALYTICS_EVENTS.CORE.NOT_FOUND]: object({
    path: string().required(),
  }),

  // Note Events
  [ANALYTICS_EVENTS.NOTE.CREATED]: object({
    user_id: string().required(),
    organization_id: string().required(),
    note_type: string().required(),
    content_length: number().required(),
    phone_number: string().required(),
    note_id: string().required(),
    player_id: string().required(),
    title: string().optional(),
    body: string().required(),
    is_shared: boolean().required(),
    device_name: string().required(),
    device_os: string().required(),
    creation_source: string().required(),
    platform: string().required(),
    created_at: string().required(),
    updated_at: string().required(),
  }),

  [ANALYTICS_EVENTS.NOTE.DELETED]: object({
    note_id: string().required(),
    player_id: string().required(),
    title: string().optional(),
    body: string().required(),
    is_public: boolean().required(),
    created_at: string().required(),
    updated_at: string().required(),
  }),

  // User Events
  [ANALYTICS_EVENTS.USER.SIGN_IN]: object({
    method: string().required(),
    phone_number: string().required(),
    device_name: string().required(),
    device_os: string().required(),
    success: boolean().required(),
    device_type: string().required().oneOf(["mobile", "web"]),
    platform: string().required(),
    timestamp: string().required(),
  }),

  [ANALYTICS_EVENTS.USER.SIGN_OUT]: object({
    user_id: string().required(),
    organization_id: string().required(),
    phone_number: string().required(),
    reason: string().optional(),
    device_name: string().required(),
    device_os: string().required(),
    device_type: string().required().oneOf(["mobile", "web"]),
    platform: string().required(),
    timestamp: string().required(),
  }),

  [ANALYTICS_EVENTS.USER.ORGANIZATION_JOINED]: object({
    user_id: string().required(),
    organization_id: string().required(),
    organization_name: string().required(),
    phone_number: string().required(),
    is_admin: boolean().required(),
    user_role: string().required(),
    currency_code: string().required(),
    sport_id: string().required(),
    sport_name: string().required(),
    timestamp: string().required(),
  }),

  [ANALYTICS_EVENTS.USER.ORGANIZATION_LEFT]: object({
    user_id: string().required(),
    organization_id: string().required(),
    organization_name: string().required(),
    phone_number: string().required(),
    is_admin: boolean().required(),
    user_role: string().required(),
    left_at: string().required(),
    reason: string().optional(),
  }),

  [ANALYTICS_EVENTS.USER.PLAYER_WATCHLIST_ADDED]: object({
    user_id: string().required(),
    organization_id: string().required(),
    phone_number: string().required(),
    watchlist_id: string().required(),
    watchlist_name: string().required(),
    player_id: string().required(),
    player_name: string().optional(),
    action: string().oneOf(["added"]).required(),
    platform: string().required(),
    timestamp: string().required(),
  }),

  [ANALYTICS_EVENTS.USER.PLAYER_WATCHLIST_REMOVED]: object({
    user_id: string().required(),
    organization_id: string().required(),
    phone_number: string().required(),
    watchlist_id: string().required(),
    watchlist_name: string().required(),
    player_id: string().required(),
    player_name: string().optional(),
    action: string().oneOf(["removed"]).required(),
    platform: string().required(),
    timestamp: string().required(),
  }),

  [ANALYTICS_EVENTS.USER.WATCHLIST_CREATED]: object({
    user_id: string().required(),
    organization_id: string().required(),
    phone_number: string().required(),
    watchlist_id: string().required(),
    watchlist_name: string().required(),
    action: string().oneOf(["created"]).required(),
    platform: string().required(),
    timestamp: string().required(),
  }),

  [ANALYTICS_EVENTS.USER.WATCHLIST_DELETED]: object({
    user_id: string().required(),
    organization_id: string().required(),
    phone_number: string().required(),
    watchlist_id: string().required(),
    watchlist_name: string().required(),
    action: string().oneOf(["deleted"]).required(),
    platform: string().required(),
    timestamp: string().required(),
  }),

  [ANALYTICS_EVENTS.USER.WATCHLIST_UPDATED]: object({
    user_id: string().required(),
    organization_id: string().required(),
    phone_number: string().required(),
    watchlist_id: string().required(),
    watchlist_name: string().required(),
    action: string().oneOf(["updated"]).required(),
    platform: string().required(),
    timestamp: string().required(),
  }),

  [ANALYTICS_EVENTS.USER.ORGANIZATION_CREATED]: object({
    organization_id: string().required(),
  }),

  [ANALYTICS_EVENTS.USER.ORGANIZATION_UPDATED]: object({
    organization_id: string().required(),
  }),

  [ANALYTICS_EVENTS.USER.INVITED]: object({
    invited_role: string().required(),
  }),

  // `from_team_id` / `to_team_id` use `nullable()` (rather than the `required()`
  // default that the rest of this map uses) because clear-team fires with
  // `to_team_id: null`. The cast keeps the heterogeneous schema map's
  // `Record<string, ReturnType<typeof object>>` type happy — yup widens
  // strict object schemas in a way that doesn't unify with the looser ones
  // above.
  [ANALYTICS_EVENTS.USER.TEAM_SWITCHED]: object({
    user_id: string().required(),
    organization_id: string().required(),
    mode: string().oneOf(["hidden", "linked", "all"]).required(),
    from_team_id: string().nullable().defined(),
    to_team_id: string().nullable().defined(),
    timestamp: string().required(),
  }) as unknown as ReturnType<typeof object>,

  // Player Events (SE-4010)
  [ANALYTICS_EVENTS.PLAYER.PROFILE_VIEWED]: object({
    player_id: string().required(),
  }),

  [ANALYTICS_EVENTS.PLAYER.COMPARISON_VIEWED]: object({
    player_id: string().required(),
  }),

  [ANALYTICS_EVENTS.PLAYER.SIMILAR_PLAYERS_VIEWED]: object({
    player_id: string().required(),
  }),

  // Search Events (SE-4010)
  [ANALYTICS_EVENTS.SEARCH.PERFORMED]: object({
    query_length: number().required(),
  }),

  [ANALYTICS_EVENTS.SEARCH.FILTER_APPLIED]: object({
    filter_type: string().required(),
  }),

  // Scouting Events (SE-4010)
  [ANALYTICS_EVENTS.SCOUTING.KANBAN_BOARD_VIEWED]: object({
    board_id: string().required(),
  }),

  [ANALYTICS_EVENTS.SCOUTING.MATCH_INTAKE_STARTED]: object({
    player_id: string().required(),
    match_count: number().required(),
  }),

  [ANALYTICS_EVENTS.SCOUTING.REPORT_OPENED]: object({
    report_id: string().required(),
  }),

  [ANALYTICS_EVENTS.SCOUTING.SHADOW_TEAM_VIEWED]: object({
    shadow_team_id: string().required(),
  }),

  [ANALYTICS_EVENTS.SCOUTING.KNOWLEDGE_GRAPH_OPENED]: object({
    layout: string().oneOf(["desktop", "mobile"]).required(),
  }),

  // Surface Events (SE-4010)
  [ANALYTICS_EVENTS.SURFACE.HOME_VIEWED]: object({
    layout: string().oneOf(["desktop", "mobile"]).required(),
  }),

  [ANALYTICS_EVENTS.SURFACE.ACTIVITY_FEED_VIEWED]: object({
    category_filter: string().required(),
  }),

  [ANALYTICS_EVENTS.SURFACE.WATCHLIST_VIEWED]: object({
    layout: string().oneOf(["desktop", "mobile"]).required(),
  }),

  [ANALYTICS_EVENTS.SURFACE.ROSTER_VIEWED]: object({
    source: string().required(),
  }),

  [ANALYTICS_EVENTS.SURFACE.RECOMMENDATIONS_VIEWED]: object({
    category: string().required(),
  }),

  // Pipeline Events (SE-5508)
  //
  // `outcome` is the one nullable property in this block (a non-terminal move
  // has no outcome), so it uses `nullable().defined()` and the same cast the
  // TEAM_SWITCHED schema above needs — yup widens strict object schemas with a
  // nullable member in a way that doesn't unify with the looser sibling
  // schemas in this record.
  [ANALYTICS_EVENTS.PIPELINE.CREATED]: object({
    source: string().oneOf(["template", "custom", "from_migration"]).required(),
    template: string().required(),
    board_id: string().required(),
  }),

  [ANALYTICS_EVENTS.PIPELINE.STAGE_EDITED]: object({
    action: string()
      .oneOf([
        "add",
        "rename",
        "reorder",
        "delete",
        "set_protected",
        "set_terminal",
        "set_time_target",
      ])
      .required(),
    template_or_custom: string().required(),
    protected_flag_touched: boolean().required(),
    board_id: string().required(),
  }),

  [ANALYTICS_EVENTS.PIPELINE.CARD_ADDED]: object({
    source: string().oneOf(["suggested", "add_dialog", "drag"]).required(),
    stage: string().required(),
    board_id: string().required(),
  }),

  [ANALYTICS_EVENTS.PIPELINE.CARD_MOVED]: object({
    from: string().required(),
    to: string().required(),
    method: string().oneOf(["drag", "stage_picker"]).required(),
    sensitive: boolean().required(),
    outcome: string().nullable().defined(),
    board_id: string().required(),
  }) as unknown as ReturnType<typeof object>,

  [ANALYTICS_EVENTS.PIPELINE.CARD_REMOVED]: object({
    reason_given: boolean().required(),
    board_id: string().required(),
  }),

  // `reason_categories` is `defined()` rather than `required()` because a
  // candidate with no reason categories joins to the empty string, and yup
  // treats "" as missing under `required()`.
  [ANALYTICS_EVENTS.PIPELINE.SUGGESTION_ACTED]: object({
    action: string().oneOf(["add", "dismiss"]).required(),
    reason_categories: string().defined(),
    board_id: string().required(),
  }),

  [ANALYTICS_EVENTS.PIPELINE.RULE_CREATED]: object({
    kind: string().required(),
    trigger: string().required(),
    action: string().required(),
    board_id: string().required(),
  }),

  [ANALYTICS_EVENTS.PIPELINE.REVIEW_ITEM_RESOLVED]: object({
    decision: string().oneOf(["accept", "decline"]).required(),
    type: string().required(),
    age_hours: number().required(),
    board_id: string().required(),
  }),

  [ANALYTICS_EVENTS.PIPELINE.VIEW_TOGGLED]: object({
    view: string().required(),
    board_id: string().required(),
  }),

  [ANALYTICS_EVENTS.PIPELINE.LOAD_TIME_MS]: object({
    duration_ms: number().required(),
    card_count: number().required(),
    board_id: string().required(),
  }),

  [ANALYTICS_EVENTS.PIPELINE.TIER_BLOCK_RENDER]: object({
    surface: string().oneOf(["nav", "route"]).required(),
    role: string().required(),
  }),

  // Notification Events (SE-4010)
  [ANALYTICS_EVENTS.NOTIFICATION.TAPPED]: object({
    activity_type: string().required(),
  }),

  [ANALYTICS_EVENTS.NOTIFICATION.PREFERENCE_UPDATED]: object({
    activity_type_code: string().required(),
    enabled: boolean().required(),
  }),

  // Profiler Events
  [ANALYTICS_EVENTS.PROFILER.RENDER]: object({
    id: string().required(),
    phase: string().oneOf(["mount", "update", "nested-update"]).required(),
    actualDuration: number().required(),
    baseDuration: number().required(),
    startTime: number().required(),
    commitTime: number().required(),
  }),
};

/**
 * Validates an analytics event against its schema.
 * @param eventName - The name of the event to validate
 * @param parameters - The event parameters to validate
 * @returns Promise resolving to true if valid, false if invalid or no schema exists
 * @example
 * ```typescript
 * const isValid = await validateEvent('screen_view', {
 *   screen_name: 'Home',
 *   screen_class: 'HomeScreen'
 * });
 * ```
 */
export async function validateEvent(
  eventName: string,
  parameters: TEventParameter
): Promise<boolean> {
  const schema = VALIDATION_SCHEMAS[eventName];

  if (!schema) {
    console.error(`No validation schema found for event: ${eventName}`);
    return false;
  }

  try {
    await schema.validate(parameters, {
      abortEarly: false,
      strict: true,
    });
    return true;
  } catch (error) {
    console.error(
      `Validation failed for event ${eventName} with params ${JSON.stringify(parameters)}:`,
      error
    );
    return false;
  }
}
